import type { InferOutput } from 'valibot'
import { picklist } from 'valibot'

const GUIDE_TOKEN = 'veileder'

export const templateVariantSchema = picklist([
  'mal',
  'veileder',
])

export type TemplateVariant = InferOutput<typeof templateVariantSchema>

export function encodeS3KeyPath(key: string) {
  return encodeURIComponent(key.replace(/^\/+/, ''))
}

export function normalizeStringArray(input: unknown): string[] {
  if (Array.isArray(input)) {
    return input.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
  }
  if (typeof input === 'string' && input.trim().length > 0) {
    return [input.trim()]
  }
  return []
}

function isGuideFile(fileName: string): boolean {
  return fileName.toLowerCase().includes(GUIDE_TOKEN)
}

function findTemplateFileKey(files: unknown, predicate: (key: string) => boolean): string {
  const match = normalizeStringArray(files).find(predicate)
  return match ? match.replace(/^\/+/, '') : ''
}

function toTemplateFileUrl(key: string): string {
  return key ? `/api/templates/${encodeS3KeyPath(key)}` : ''
}

function hasNonGuideFile(files: unknown): boolean {
  return normalizeStringArray(files).some((key) => !isGuideFile(key))
}

function getMainTemplateFileKey(files: unknown, ext: string): string {
  const matchesExt = (f: string) => f.toLowerCase().endsWith(ext)
  if (hasNonGuideFile(files)) {
    return findTemplateFileKey(files, (key) => matchesExt(key) && !isGuideFile(key))
  }

  return findTemplateFileKey(files, matchesExt)
}

function getGuideFileKey(files: unknown, ext: string): string {
  if (!hasNonGuideFile(files)) {
    return ''
  }

  return findTemplateFileKey(files, (f) => f.toLowerCase().endsWith(ext) && isGuideFile(f))
}

export function getTemplatePdfUrl(files: unknown): string {
  return toTemplateFileUrl(getMainTemplateFileKey(files, '.pdf'))
}

export function getTemplateDocxKey(files: unknown): string {
  return getMainTemplateFileKey(files, '.docx')
}

export function getTemplateGuidePreviewUrl(files: unknown): string {
  return toTemplateFileUrl(getGuideFileKey(files, '.pdf'))
}

export function getTemplateGuideWordKey(files: unknown): string {
  return getGuideFileKey(files, '.docx')
}

export function getTemplateDownloadUrl(id: string, variant: TemplateVariant): string {
  return `/api/download/templates/${encodeURIComponent(id)}?variant=${variant}`
}
